import type { DrawingsDocument } from "@luxalgo/vela";

const STORAGE_PREFIX = "tessera.tradeReview.velaDrawings.v1:";

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function storageKey(symbol: string, candleType: string): string {
  const sym = symbol.trim().toUpperCase() || "_";
  const tf = candleType.trim().toLowerCase() || "_";
  return `${STORAGE_PREFIX}${sym}:${tf}`;
}

/** Persist user drawings for a symbol + Tessera candle TF (local only). */
export function saveVelaDrawingsDocument(
  symbol: string,
  candleType: string,
  doc: DrawingsDocument | { version?: number; drawings?: unknown[] } | null | undefined
): void {
  if (!canUseLocalStorage() || !symbol.trim()) return;
  try {
    const key = storageKey(symbol, candleType);
    const rawDrawings = doc && Array.isArray(doc.drawings) ? doc.drawings : null;
    if (!rawDrawings || rawDrawings.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    // Strip Tessera-managed overlay drawings (prefix) so they are never restored as user art.
    const drawings = rawDrawings.filter(
      (d): d is DrawingsDocument["drawings"][number] =>
        !!d &&
        typeof d === "object" &&
        typeof (d as { id?: unknown }).id === "string" &&
        !(d as { id: string }).id.startsWith("tessera-")
    );
    if (drawings.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify({ version: 1, drawings }));
  } catch {
    // ignore quota / private mode
  }
}

export function loadVelaDrawingsDocument(
  symbol: string,
  candleType: string
): DrawingsDocument | null {
  if (!canUseLocalStorage() || !symbol.trim()) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(symbol, candleType));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DrawingsDocument;
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.drawings)) return null;
    return {
      version: 1,
      drawings: parsed.drawings.filter(
        (d) => typeof d?.id === "string" && !d.id.startsWith("tessera-")
      ),
    };
  } catch {
    return null;
  }
}

const APPEARANCE_KEY = "tessera.tradeReview.appearance.v1";

export type TradeReviewAppearancePrefs = {
  candleUp?: string;
  candleDown?: string;
  overlayColors?: Record<string, string>;
};

export function loadTradeReviewAppearancePrefs(): TradeReviewAppearancePrefs {
  if (!canUseLocalStorage()) return {};
  try {
    const raw = window.localStorage.getItem(APPEARANCE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as TradeReviewAppearancePrefs;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveTradeReviewAppearancePrefs(prefs: TradeReviewAppearancePrefs): void {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(APPEARANCE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}
